import React, { useState, useEffect } from "react";
import { getApiUrl } from "../../../../../../../../config/api";

const DICT = {
    title: { en: "Allowed Origins (CORS)", zh: "允許的來源網址 (CORS)" },
    desc: {
        en: "Only origins in this list may call this channel's public API. Leave empty to allow all origins.",
        zh: "只有清單中的來源網址可以呼叫此頻道的公開 API。留空表示允許所有來源。",
    },
    addUrl: { en: "Add URL", zh: "新增網址" },
    urlPh: { en: "https://example.com", zh: "https://example.com" },
    save: { en: "Save Changes", zh: "儲存變更" },
    saving: { en: "Saving…", zh: "儲存中…" },
    saved: { en: "Saved!", zh: "已儲存！" },
    saveError: { en: "Failed to save.", zh: "儲存失敗。" },
    invalidUrl: { en: "Please enter a valid URL.", zh: "請輸入有效的網址。" },
    duplicate: { en: "URL already in list.", zh: "此網址已在清單中。" },
    allowAll: {
        en: "All origins allowed (no restrictions)",
        zh: "允許所有來源（無限制）",
    },
    wildcardNote: {
        en: 'The list contains "*" — all origins are explicitly allowed.',
        zh: "清單包含「*」，表示明確允許所有來源。",
    },
};

const t = (key, lang) => DICT[key]?.[lang] || DICT[key]?.en || key;

export default function AllowedUrlsView({ channel, language, currentUser }) {
    const [urls, setUrls] = useState(channel.allowed_url || []);
    const [input, setInput] = useState("");
    const [inputError, setInputError] = useState("");
    const [status, setStatus] = useState("idle"); // idle | saving | saved | error
    const [errorMsg, setErrorMsg] = useState("");
    const [dirty, setDirty] = useState(false);

    // Reset when the channel changes. Keyed on channel_id only: re-running on
    // every new `allowed_url` array identity would discard unsaved edits.
    useEffect(() => {
        setUrls(channel.allowed_url || []);
        setDirty(false);
        setStatus("idle");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel.channel_id]);

    const isValidUrl = (v) => {
        if (v === "*") return true;
        try {
            new URL(v);
            return true;
        } catch {
            return false;
        }
    };

    const addUrl = () => {
        const val = input.trim().replace(/\/+$/, ""); // strip trailing slash
        if (!val) return;
        if (!isValidUrl(val)) {
            setInputError(t("invalidUrl", language));
            return;
        }
        if (urls.includes(val)) {
            setInputError(t("duplicate", language));
            return;
        }
        setUrls((prev) => [...prev, val]);
        setInput("");
        setInputError("");
        setDirty(true);
        setStatus("idle");
    };

    const removeUrl = (url) => {
        setUrls((prev) => prev.filter((u) => u !== url));
        setDirty(true);
        setStatus("idle");
    };

    const handleSave = async () => {
        setStatus("saving");
        setErrorMsg("");
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(
                `${getApiUrl()}/api/v2/channels/${channel.channel_id}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ allowed_url: urls }),
                },
            );
            const data = await res.json();
            if (data.success) {
                setStatus("saved");
                setDirty(false);
                setTimeout(() => setStatus("idle"), 2500);
            } else {
                setStatus("error");
                setErrorMsg(data.error || t("saveError", language));
            }
        } catch (err) {
            setStatus("error");
            setErrorMsg(err.message || t("saveError", language));
        }
    };

    const hasWildcard = urls.includes("*");

    return (
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Description */}
            <p className="text-sm text-gray-500">{t("desc", language)}</p>

            {/* Input row */}
            <div className="space-y-2">
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            setInputError("");
                        }}
                        onKeyDown={(e) =>
                            e.key === "Enter" && (e.preventDefault(), addUrl())
                        }
                        placeholder={t("urlPh", language)}
                        className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
                    />
                    <button
                        type="button"
                        onClick={addUrl}
                        className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-xl transition-colors whitespace-nowrap"
                    >
                        {t("addUrl", language)}
                    </button>
                </div>
                {inputError && (
                    <p className="text-xs text-red-500">{inputError}</p>
                )}
                <p className="text-xs text-gray-400">
                    {language === "zh"
                        ? "輸入完整來源，例如 https://example.com（不含路徑）。輸入 * 表示允許所有來源。"
                        : "Enter the full origin e.g. https://example.com (no path). Use * to explicitly allow all."}
                </p>
            </div>

            {/* URL list */}
            {urls.length === 0 ? (
                <div className="flex items-center gap-2.5 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                    <svg
                        className="w-4 h-4 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    {t("allowAll", language)}
                </div>
            ) : hasWildcard ? (
                <div className="flex items-center gap-2.5 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm">
                    <svg
                        className="w-4 h-4 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    {t("wildcardNote", language)}
                </div>
            ) : null}

            {urls.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                    {urls.map((url) => (
                        <div
                            key={url}
                            className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 group transition-colors"
                        >
                            <div className="flex items-center gap-2.5 min-w-0">
                                {url === "*" ? (
                                    <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <svg
                                            className="w-3 h-3 text-blue-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064"
                                            />
                                        </svg>
                                    </span>
                                ) : (
                                    <span className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                        <svg
                                            className="w-3 h-3 text-orange-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                                            />
                                        </svg>
                                    </span>
                                )}
                                <span className="text-sm text-gray-700 font-mono truncate">
                                    {url}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeUrl(url)}
                                className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100 p-1 rounded"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Save feedback */}
            {status === "error" && (
                <p className="text-sm text-red-500">{errorMsg}</p>
            )}

            {/* Save button */}
            <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-gray-400">
                    {urls.length === 0
                        ? language === "zh"
                            ? "目前允許所有來源"
                            : "Currently open to all origins"
                        : language === "zh"
                          ? `${urls.length} 個來源已設定`
                          : `${urls.length} origin${urls.length > 1 ? "s" : ""} configured`}
                </p>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={!dirty || status === "saving"}
                    className={`px-5 py-2 text-sm font-medium rounded-xl transition-all ${
                        status === "saved"
                            ? "bg-green-500 text-white"
                            : dirty
                              ? "bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
                              : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                >
                    {status === "saving" ? (
                        <span className="flex items-center gap-2">
                            <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            {t("saving", language)}
                        </span>
                    ) : status === "saved" ? (
                        <span className="flex items-center gap-1.5">
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2.5}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                            {t("saved", language)}
                        </span>
                    ) : (
                        t("save", language)
                    )}
                </button>
            </div>
        </div>
    );
}
